/**
 * Helpers and builders for schema.org JSON-LD (Hotel, Organization, Offer, ItemList, HotelRoom).
 */

export function normalizeSiteOrigin(siteUrl) {
  if (!siteUrl || typeof siteUrl !== 'string') return ''
  return siteUrl.replace(/\/+$/, '')
}

/** Match SEO canonical path handling in components/SEO */
export function canonicalPageUrl(siteUrl, pathname) {
  const origin = normalizeSiteOrigin(siteUrl)
  const path = (pathname || '/').replace(/\/$|$/, '/')
  return origin + path
}

export function omitEmpty(value) {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'string' && !value.trim()) return undefined
  if (Array.isArray(value)) {
    const mapped = value.map(omitEmpty).filter(x => x != null)
    return mapped.length ? mapped : undefined
  }
  if (typeof value === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(value)) {
      const o = omitEmpty(v)
      if (o != null) out[k] = o
    }
    return Object.keys(out).length ? out : undefined
  }
  return value
}

function postalAddressFromGlobals(globals) {
  const street = globals?.address
  if (!street || !String(street).trim()) return undefined
  return omitEmpty({
    '@type': 'PostalAddress',
    streetAddress: String(street).trim(),
    addressCountry: 'NZ',
  })
}

function sameAsFromGlobals(globals) {
  const urls = [globals?.facebook, globals?.instagram].filter(
    u => u && String(u).trim()
  )
  return urls.length ? urls.map(u => String(u).trim()) : undefined
}

function defaultOgImageUrl(siteMetadata) {
  const origin = normalizeSiteOrigin(siteMetadata?.siteUrl)
  if (!origin) return undefined
  const img = siteMetadata?.image
  if (!img) return undefined
  const path = String(img).replace(/^\//, '')
  return `${origin}/${path}`
}

/**
 * Sitewide @graph: Organization, Hotel, Offer (via Hotel.makesOffer).
 * @param {{ siteUrl: string, title: string, description?: string, image?: string }} siteMetadata
 * @param {object} globals — prismicGlobals.data (may be null)
 */
export function buildSitewideGraph({ siteMetadata, globals }) {
  const origin = normalizeSiteOrigin(siteMetadata?.siteUrl)
  if (!origin) return null

  const orgId = `${origin}/#organization`
  const hotelId = `${origin}/#hotel`

  const orgName =
    (globals?.title && String(globals.title).trim()) ||
    siteMetadata?.title ||
    'Organization'
  const hotelName = orgName

  const postalAddress = postalAddressFromGlobals(globals)
  const sameAs = sameAsFromGlobals(globals)
  const defaultImage = defaultOgImageUrl(siteMetadata)

  const contactPoint = omitEmpty({
    '@type': 'ContactPoint',
    contactType: 'customer service',
    telephone: globals?.phone_number || undefined,
    email: globals?.email || undefined,
  })

  const organization = omitEmpty({
    '@type': 'Organization',
    '@id': orgId,
    name: orgName,
    url: `${origin}/`,
    sameAs,
    address: postalAddress,
    contactPoint: contactPoint ? [contactPoint] : undefined,
    telephone: globals?.phone_number || undefined,
    email: globals?.email || undefined,
  })

  const bookingUrl =
    globals?.booking_button?.url && String(globals.booking_button.url).trim()
      ? String(globals.booking_button.url).trim()
      : undefined

  const offerName =
    (globals?.booking_button_text &&
      String(globals.booking_button_text).trim()) ||
    'Book direct'

  const offer = bookingUrl
    ? omitEmpty({
        '@type': 'Offer',
        '@id': `${origin}/#book-offer`,
        name: offerName,
        url: bookingUrl,
      })
    : undefined

  const hotel = omitEmpty({
    '@type': 'Hotel',
    '@id': hotelId,
    name: hotelName,
    url: `${origin}/`,
    parentOrganization: { '@id': orgId },
    address: postalAddress,
    telephone: globals?.phone_number || undefined,
    email: globals?.email || undefined,
    image: defaultImage ? [defaultImage] : undefined,
    makesOffer: offer || undefined,
  })

  const graph = omitEmpty([organization, hotel].filter(Boolean))
  if (!graph || !graph.length) return null

  return omitEmpty({
    '@context': 'https://schema.org',
    '@graph': graph,
  })
}

function pickRoomImageUrl(roomData) {
  if (!roomData) return undefined
  if (roomData.image2?.url) return roomData.image2.url
  const firstPanel = roomData.section_3_image && roomData.section_3_image[0]
  if (firstPanel?.image1?.url) return firstPanel.image1.url
  return undefined
}

/**
 * ItemList of HotelRoom for the rooms index page.
 * @param {{ siteUrl: string, listPageUrl: string, listTitle?: string, rooms: Array }} params
 */
export function buildRoomsItemListSchema({
  siteUrl,
  listPageUrl,
  listTitle,
  rooms,
}) {
  const origin = normalizeSiteOrigin(siteUrl)
  const hotelId = origin ? `${origin}/#hotel` : undefined
  const baseListUrl = listPageUrl.split('#')[0]

  const itemListElement = []
  let position = 0

  for (const entry of rooms || []) {
    const doc = entry?.Room?.document
    // UID lives on the Prismic document link (`Room.uid`), not always on the nested fragment.
    const roomUid = entry?.Room?.uid ?? doc?.uid
    if (!roomUid || !doc?.data?.title) continue
    position += 1
    const data = doc.data
    const fragmentUrl = `${baseListUrl}#${roomUid}`

    const hotelRoom = omitEmpty({
      '@type': 'HotelRoom',
      name: data.title,
      description: data.summary || undefined,
      image: pickRoomImageUrl(data),
      url: fragmentUrl,
      containedInPlace: hotelId ? { '@id': hotelId } : undefined,
    })

    itemListElement.push(
      omitEmpty({
        '@type': 'ListItem',
        position,
        item: hotelRoom,
      })
    )
  }

  if (!itemListElement.length) return null

  return omitEmpty({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listTitle || 'Rooms',
    url: baseListUrl,
    numberOfItems: itemListElement.length,
    itemListElement,
  })
}

const DEFAULT_BOOKING_OFFER_URL =
  'https://book-directonline.com/properties/mipadhoteldirect'

/**
 * ItemList of Offer entities from Prismic pages `offers` slice(s).
 * Matches fields queried on PrismicPagesBodyOffers (see templates/pages.js).
 */
export function buildOffersItemListSchema({ siteUrl, pageUrl, body }) {
  if (!body || !Array.isArray(body)) return null

  const offersSlices = body.filter(
    s => s && s.slice_type === 'offers' && s.items && s.items.length
  )
  if (!offersSlices.length) return null

  const origin = normalizeSiteOrigin(siteUrl)
  const hotelId = origin ? `${origin}/#hotel` : undefined
  const basePageUrl = pageUrl.split('#')[0]

  let listTitle = 'Offers'
  for (const slice of offersSlices) {
    const heading = slice.primary?.title?.text
    if (heading && String(heading).trim()) {
      listTitle = String(heading).trim()
      break
    }
  }

  const itemListElement = []
  let position = 0

  for (const slice of offersSlices) {
    for (const row of slice.items) {
      const name = row?.name?.text && String(row.name.text).trim()
      if (!name) continue
      position += 1
      const desc =
        row?.text?.text && String(row.text.text).trim()
          ? String(row.text.text).trim()
          : undefined
      const imageUrl =
        row?.image?.url && String(row.image.url).trim()
          ? String(row.image.url).trim()
          : undefined
      const bookUrlRaw = row?.book_link?.url && String(row.book_link.url).trim()
      const bookUrl = bookUrlRaw || DEFAULT_BOOKING_OFFER_URL

      const offer = omitEmpty({
        '@type': 'Offer',
        name,
        description: desc,
        image: imageUrl,
        url: bookUrl,
        offeredBy: hotelId ? { '@id': hotelId } : undefined,
      })

      itemListElement.push(
        omitEmpty({
          '@type': 'ListItem',
          position,
          item: offer,
        })
      )
    }
  }

  if (!itemListElement.length) return null

  return omitEmpty({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listTitle,
    url: basePageUrl,
    numberOfItems: itemListElement.length,
    itemListElement,
  })
}
